import React from 'react';
import Iconify from 'src/components/Iconify';
import { DOCUMENTO_DESIGN as D } from '../EditorDocumento.design';

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function calcularItem(item) {
  const quantidade = Number(item.quantidade) || 0;
  const precoUnitario = Number(item.preco_unitario) || 0;
  const descontoPercent = Number(item.desconto_percent) || 0;
  const bruto = quantidade * precoUnitario;
  return bruto - (bruto * descontoPercent / 100);
}

// Input de moeda no padrão brasileiro: o usuário digita os centavos da direita para a
// esquerda (ex.: digitar "125000" vira "R$ 1.250,00"). Guarda o valor numérico.
function CurrencyInput({ value, onChange, className, ariaLabel }) {
  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    const num = digits ? parseInt(digits, 10) / 100 : 0;
    onChange(num);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      value={formatCurrency(value)}
      onChange={handleChange}
      className={className}
      aria-label={ariaLabel}
    />
  );
}

const somenteInteiro = (texto) => parseInt(String(texto).replace(/\D/g, ''), 10) || 0;

const COLS = 'grid grid-cols-[1fr_100px_132px_76px_106px_32px] gap-2 items-center';

export default function ItensTableEditavel({ itens, descontoGlobalPercent, onChange, readOnly }) {
  const lista = itens || [];

  const updateItem = (index, field, value) => {
    onChange({ itens: lista.map((item, i) => i === index ? { ...item, [field]: value } : item) });
  };

  const addItem = () => {
    onChange({ itens: [...lista, { descricao: '', quantidade: 1, preco_unitario: 0, desconto_percent: 0 }] });
  };

  const removeItem = (index) => {
    onChange({ itens: lista.filter((_, i) => i !== index) });
  };

  const subtotal = lista.reduce((acc, item) => acc + calcularItem(item), 0);
  const valorTotal = subtotal - (subtotal * (Number(descontoGlobalPercent) || 0) / 100);

  // --- Modo leitura (proposta já enviada; também a prévia e a miniatura) ---
  if (readOnly) {
    return (
      <div className="mt-2">
        <div className={`${COLS} px-2 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-[#ABABAD]`}>
          <span>Produto ou serviço</span>
          <span className="text-center">Qtd.</span>
          <span className="text-right">Preço unit.</span>
          <span className="text-right">Desc.</span>
          <span className="text-right">Total</span>
          <span />
        </div>
        {lista.map((item, index) => (
          <div key={index} className={`${COLS} px-2 py-2 border-t border-gray-100 dark:border-[#383838]`}>
            <span className="text-sm text-gray-800 dark:text-[#D1D2D3]">{item.descricao || 'Item sem descrição'}</span>
            <span className="text-sm text-center text-gray-600 dark:text-[#ABABAD]">{item.quantidade}</span>
            <span className="text-sm text-right text-gray-600 dark:text-[#ABABAD]">{formatCurrency(item.preco_unitario)}</span>
            <span className="text-sm text-right text-gray-600 dark:text-[#ABABAD]">{item.desconto_percent || 0}%</span>
            <span className="text-sm text-right font-medium text-gray-900 dark:text-[#D1D2D3]">{formatCurrency(calcularItem(item))}</span>
            <span />
          </div>
        ))}
        <ResumoTotais subtotal={subtotal} descontoGlobalPercent={descontoGlobalPercent} valorTotal={valorTotal} readOnly onChange={onChange} />
      </div>
    );
  }

  // --- Modo edição: tabela de documento com campos silenciosos ---
  return (
    <div className="mt-2">
      <div className={`${COLS} px-1 pb-2 text-xs text-gray-500 border-b border-gray-200`}>
        <span>Produto ou serviço</span>
        <span className="text-center">Qtd.</span>
        <span className="text-right pr-2">Preço unit.</span>
        <span className="text-right pr-2">Desc.</span>
        <span className="text-right pr-1">Total</span>
        <span />
      </div>

      {lista.map((item, index) => (
        <div key={index} className={`${COLS} border-b border-gray-100 py-0.5 group`}>
          <input
            value={item.descricao || ''}
            onChange={e => updateItem(index, 'descricao', e.target.value)}
            placeholder="Produto ou serviço"
            aria-label={`Descrição do item ${index + 1}`}
            className={D.docQuiet}
          />
          <input
            inputMode="numeric"
            value={item.quantidade}
            onChange={e => updateItem(index, 'quantidade', somenteInteiro(e.target.value))}
            aria-label={`Quantidade do item ${index + 1}`}
            className={`${D.docQuiet} text-center tabular-nums`}
          />
          <CurrencyInput
            value={item.preco_unitario}
            onChange={v => updateItem(index, 'preco_unitario', v)}
            ariaLabel={`Preço unitário do item ${index + 1}`}
            className={`${D.docQuiet} text-right tabular-nums`}
          />
          <div className="relative">
            <input
              inputMode="numeric"
              value={item.desconto_percent || 0}
              onChange={e => updateItem(index, 'desconto_percent', Math.min(100, somenteInteiro(e.target.value)))}
              aria-label={`Desconto do item ${index + 1} em porcentagem`}
              className={`${D.docQuiet} text-right tabular-nums pr-6`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
          </div>
          <span className="text-sm text-right font-semibold text-gray-900 tabular-nums pr-1">
            {formatCurrency(calcularItem(item))}
          </span>
          <button
            type="button"
            onClick={() => removeItem(index)}
            className={`${D.blockPillDanger} opacity-0 group-hover:opacity-100 focus-visible:opacity-100 justify-self-center`}
            title="Remover item"
            aria-label={`Remover item ${index + 1}`}
          >
            <Iconify icon="eva:close-outline" width={15} />
          </button>
        </div>
      ))}

      <button type="button" onClick={addItem} className={`${D.addBlock} mt-2`}>
        <Iconify icon="eva:plus-fill" width={16} />
        Adicionar item
      </button>

      <ResumoTotais subtotal={subtotal} descontoGlobalPercent={descontoGlobalPercent} valorTotal={valorTotal} onChange={onChange} />
    </div>
  );
}

function ResumoTotais({ subtotal, descontoGlobalPercent, valorTotal, readOnly, onChange }) {
  return (
    <div className="flex justify-end mt-4 pt-3 border-t border-gray-200 dark:border-[#383838]">
      <div className="w-[300px] space-y-2">
        <div className="flex justify-between items-center text-sm text-gray-500 dark:text-[#ABABAD]">
          <span>Subtotal</span>
          <span className="tabular-nums">{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex justify-between items-center text-sm text-gray-500 dark:text-[#ABABAD]">
          <span>Desconto geral</span>
          {readOnly ? (
            <span>{descontoGlobalPercent || 0}%</span>
          ) : (
            <span className="relative inline-block w-20">
              <input
                inputMode="numeric"
                value={descontoGlobalPercent || 0}
                onChange={e => onChange({ desconto_global_percent: Math.min(100, somenteInteiro(e.target.value)) })}
                aria-label="Desconto geral em porcentagem"
                className={`${D.docQuiet} h-8 text-right tabular-nums pr-6`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
            </span>
          )}
        </div>
        <div className="flex justify-between items-center text-base font-bold text-gray-900 dark:text-[#D1D2D3] pt-2 border-t border-gray-100 dark:border-[#383838]">
          <span>Valor total do pedido</span>
          <span className="tabular-nums">{formatCurrency(valorTotal)}</span>
        </div>
      </div>
    </div>
  );
}
